/**
 * contractHistorySlice — append-only ledger of every contract that ever
 * appeared on the player's board. Tracks the full lifecycle :
 *   proposed → accepted (or refused / expired / displaced) → completed
 *
 * Created 08/02/2026 (soir 9) to give the user (SCD author) an objective
 * dataset to balance the game economy. Each contract instance owns exactly
 * one row, mutated in place as it transitions through statuses.
 *
 * Why a dedicated slice ?
 *   • Decoupled from `availableContracts` / `activeContracts` so we can
 *     keep history even after a contract is removed from the live arrays.
 *   • Mutated only via the actions exposed below — no other slice should
 *     read or write `contractLedger` directly.
 *   • Bounded growth : we hard-cap the ledger at 2000 entries (most recent
 *     wins). At 30 contracts/day that's ~60 days of history, plenty for
 *     balancing analysis without inflating localStorage / Zustand persist.
 *
 * The ledger row schema is documented inline on the first mutator.
 */

const LEDGER_CAP = 2000;

// Helper — find the entry index for an instanceId, or return -1.
function _findIdx(ledger, instanceId) {
  if (!ledger || !instanceId) return -1;
  for (let i = ledger.length - 1; i >= 0; i--) {
    if (ledger[i].instanceId === instanceId) return i;
  }
  return -1;
}

// Build a ledger entry from a freshly-spawned contract instance.
function _buildEntry(contract, day) {
  return {
    // Identification
    instanceId: contract.instanceId,
    contractId: contract.id,
    name: contract.name,
    category: contract.category || null,
    frequency: contract.frequency || 'ponctuel',
    tierMin: contract.tierMin || 1,
    // Money
    revenue: contract.revenue || 0,                   // total for ponctuel
    dailyRevenue: contract.dailyRevenue || 0,         // for régulier
    totalValue: contract.totalValue || contract.revenue || 0,
    duration: contract.duration || 1,
    // International / specialty flags (useful for analytics)
    international: contract.international || null,
    requiresLicense: contract.requiresLicense || null,
    requiresSpecialty: contract.requiresSpecialty || null,
    clientName: contract.clientName || null,
    // 25/07/2026 — drill-down « honorés vs laissés filer » (retour user) :
    // ville d'origine + flag hors zone (opportunité rare boostée au spawn).
    originCityId: contract.originCityId || null,
    horsZone: !!contract.rareOpportunity,
    // Lifecycle
    status: 'proposed',
    spawnDay: day || 0,
    acceptedOnDay: null,
    completedOnDay: null,
    expiredOnDay: null,
    cancelledOnDay: null,
    displacedOnDay: null,
    // Acceptance details (filled when accepted)
    acceptedRevenue: null,
    acceptedDailyRevenue: null,
    argumentationMultiplier: null,
    vehicleId: null,
    driverId: null,
    // Operational outcome
    daysServed: 0,
    missedDeliveries: 0,
  };
}

export const createContractHistorySlice = (set, get) => ({
  /**
   * Master ledger of every contract instance ever proposed. Append-only ;
   * old entries pruned when LEDGER_CAP is reached.
   */
  contractLedger: [],

  /**
   * Called when one or more brand-new contract instances appear on the
   * board (daily top-up, AO tender accepted, special spawn, …). Inserts
   * one 'proposed' row per instance. Idempotent : if the instanceId is
   * already in the ledger, the call is a no-op (some refresh paths run
   * twice on the same tick during tests).
   */
  logContractsProposed: (contracts, totalDays) => {
    if (!Array.isArray(contracts) || contracts.length === 0) return;
    const state = get();
    const ledger = state.contractLedger || [];
    const known = new Set(ledger.map(e => e.instanceId));
    const additions = contracts
      .filter(c => c && c.instanceId && !known.has(c.instanceId))
      .map(c => _buildEntry(c, totalDays || 0));
    if (additions.length === 0) return;
    const merged = [...ledger, ...additions];
    // Bound growth — keep the most recent LEDGER_CAP entries.
    const pruned = merged.length > LEDGER_CAP ? merged.slice(merged.length - LEDGER_CAP) : merged;
    set({ contractLedger: pruned });
  },

  /**
   * Mark a contract as accepted. Mutates the existing 'proposed' row in
   * place. If the row does not exist yet (race condition with a hot-fire
   * accept), we synthesise it from `contract` so the ledger stays
   * coherent.
   */
  logContractAccepted: (contract, info = {}) => {
    if (!contract || !contract.instanceId) return;
    const state = get();
    const ledger = state.contractLedger || [];
    const idx = _findIdx(ledger, contract.instanceId);
    const updates = {
      status: 'accepted',
      acceptedOnDay: info.day ?? state.totalDays ?? null,
      acceptedRevenue: info.revenue ?? contract.revenue ?? null,
      acceptedDailyRevenue: info.dailyRevenue ?? contract.dailyRevenue ?? null,
      argumentationMultiplier: info.multiplier && info.multiplier !== 1 ? info.multiplier : null,
      vehicleId: info.vehicleId || null,
      driverId: info.driverId || null,
    };
    if (idx === -1) {
      const entry = { ..._buildEntry(contract, state.totalDays || 0), ...updates };
      const merged = [...ledger, entry];
      const pruned = merged.length > LEDGER_CAP ? merged.slice(merged.length - LEDGER_CAP) : merged;
      set({ contractLedger: pruned });
      return;
    }
    const next = ledger.slice();
    next[idx] = { ...next[idx], ...updates };
    set({ contractLedger: next });
  },

  /**
   * Mark contracts as expired (dropped from the board after sitting too
   * long). Called from the daily top-up when the > 14 days rule prunes.
   * Only updates entries currently in 'proposed' status — entries already
   * accepted/completed are untouched.
   */
  logContractsExpired: (instanceIds, totalDays) => {
    if (!Array.isArray(instanceIds) || instanceIds.length === 0) return;
    const state = get();
    const ledger = state.contractLedger || [];
    const set_ = new Set(instanceIds);
    let touched = false;
    const next = ledger.map(e => {
      if (set_.has(e.instanceId) && e.status === 'proposed') {
        touched = true;
        return { ...e, status: 'expired', expiredOnDay: totalDays ?? state.totalDays ?? null };
      }
      return e;
    });
    if (touched) set({ contractLedger: next });
  },

  /**
   * Mark contracts as completed (daysRemaining hit 0). Pulls the served /
   * missed counters from the matching activeContracts row when available
   * so the ledger reflects the real operational outcome.
   */
  logContractsCompleted: (instanceIds, totalDays) => {
    if (!Array.isArray(instanceIds) || instanceIds.length === 0) return;
    const state = get();
    const ledger = state.contractLedger || [];
    const active = state.activeContracts || [];
    const set_ = new Set(instanceIds);
    let touched = false;
    const next = ledger.map(e => {
      if (set_.has(e.instanceId) && e.status === 'accepted') {
        const ac = active.find(c => c.instanceId === e.instanceId);
        touched = true;
        return {
          ...e,
          status: 'completed',
          completedOnDay: totalDays ?? state.totalDays ?? null,
          daysServed: ac ? (ac.daysServed || 0) : e.daysServed,
          missedDeliveries: ac ? (ac.missedDeliveries || 0) : e.missedDeliveries,
        };
      }
      return e;
    });
    if (touched) set({ contractLedger: next });
  },

  /**
   * Mark a contract as cancelled (user invoked cancelContract). Updates
   * served/missed counters from activeContracts before the slice removes
   * the contract.
   */
  logContractCancelled: (contract, totalDays) => {
    if (!contract || !contract.instanceId) return;
    const state = get();
    const ledger = state.contractLedger || [];
    const idx = _findIdx(ledger, contract.instanceId);
    if (idx === -1) return;
    const next = ledger.slice();
    next[idx] = {
      ...next[idx],
      status: 'cancelled',
      cancelledOnDay: totalDays ?? state.totalDays ?? null,
      daysServed: contract.daysServed || next[idx].daysServed,
      missedDeliveries: contract.missedDeliveries || next[idx].missedDeliveries,
    };
    set({ contractLedger: next });
  },

  /**
   * Mark contracts as displaced (force-reassignment of vehicle/driver to
   * another contract). Distinct from cancelled : the player didn't
   * resiliate, the system displaced them.
   */
  logContractsDisplaced: (instanceIds, totalDays) => {
    if (!Array.isArray(instanceIds) || instanceIds.length === 0) return;
    const state = get();
    const ledger = state.contractLedger || [];
    const set_ = new Set(instanceIds);
    let touched = false;
    const next = ledger.map(e => {
      if (set_.has(e.instanceId) && e.status === 'accepted') {
        touched = true;
        return { ...e, status: 'displaced', displacedOnDay: totalDays ?? state.totalDays ?? null };
      }
      return e;
    });
    if (touched) set({ contractLedger: next });
  },

  /**
   * Aggregate counters per status — used by the dashboard UI to surface
   * a quick stats banner.
   */
  getContractLedgerStats: () => {
    const ledger = (get().contractLedger || []);
    const stats = { total: ledger.length, proposed: 0, accepted: 0, completed: 0, expired: 0, cancelled: 0, displaced: 0 };
    let acceptedRevenue = 0;
    let completedRevenue = 0;
    for (const e of ledger) {
      stats[e.status] = (stats[e.status] || 0) + 1;
      if (e.status === 'accepted' || e.status === 'completed' || e.status === 'cancelled' || e.status === 'displaced') {
        acceptedRevenue += e.frequency === 'ponctuel'
          ? (e.acceptedRevenue || e.revenue || 0)
          : ((e.acceptedDailyRevenue || e.dailyRevenue || 0) * (e.duration || 0));
      }
      if (e.status === 'completed') {
        completedRevenue += e.frequency === 'ponctuel'
          ? (e.acceptedRevenue || e.revenue || 0)
          : ((e.acceptedDailyRevenue || e.dailyRevenue || 0) * (e.daysServed || e.duration || 0));
      }
    }
    stats.acceptanceRate = stats.total > 0
      ? Math.round(((stats.accepted + stats.completed + stats.cancelled + stats.displaced) / stats.total) * 100)
      : 0;
    stats.acceptedRevenue = acceptedRevenue;
    stats.completedRevenue = completedRevenue;
    return stats;
  },
});
